/**
 * Runtime Health operational domains — shared Ops + Control Plane model.
 *
 * Domains (never technology-first flat cards):
 * 1. Platform — AI Secure itself
 * 2. Tenant Runtime — selected tenant operational services
 * 3. Integrations — external dependencies
 * 4. Infrastructure — host/data-plane resources
 *
 * Reorganizes existing checks; does not invent Healthy or new probes.
 */

import type {
  CustomerStatusInput,
  DnsStatusInput,
  HealthLabel,
  IntegrationStatusInput,
  KnowledgeStatusInput,
  NotMonitoredLabel,
  RuntimeHealthRow,
  RuntimeProbeInput,
  SslStatusInput,
  TenantHealthInput,
} from "./types";
import { mapHealthLabel, resolveTenantHealthLabel } from "./health";
import { LABEL } from "./terminology";
import {
  computeFreshness,
  formatVerificationAge,
  normalizeVerificationResults,
  verificationDisplayLabel,
  type VerificationFreshness,
  type VerificationResult,
} from "./verification";

export type RuntimeHealthDomainId =
  | "platform"
  | "tenant_runtime"
  | "integrations"
  | "infrastructure";

export const RUNTIME_HEALTH_DOMAIN_ORDER: RuntimeHealthDomainId[] = [
  "platform",
  "tenant_runtime",
  "integrations",
  "infrastructure",
];

export const RUNTIME_HEALTH_DOMAIN_META: Record<
  RuntimeHealthDomainId,
  { name: string; question: string }
> = {
  platform: {
    name: "Platform",
    question: "Is AI Secure itself healthy?",
  },
  tenant_runtime: {
    name: "Tenant Runtime",
    question: "Is this tenant operational?",
  },
  integrations: {
    name: "Integrations",
    question: "Are external dependencies verified?",
  },
  infrastructure: {
    name: "Infrastructure",
    question: "Are host and data-plane resources healthy?",
  },
};

/**
 * Stable mapping: legacy Runtime Health tile → operational domain.
 * Every existing check maps to exactly one domain (no duplicates).
 */
export const LEGACY_RUNTIME_HEALTH_MAPPING = [
  {
    oldId: "api",
    oldName: "API",
    domain: "tenant_runtime" as const,
    newId: "concierge_runtime",
    newName: "Concierge Runtime",
  },
  {
    oldId: "database",
    oldName: "Database",
    domain: "infrastructure" as const,
    newId: "postgresql",
    newName: "PostgreSQL",
  },
  {
    oldId: "ai",
    oldName: "AI Provider",
    domain: "integrations" as const,
    newId: "openai",
    newName: "OpenAI",
  },
  {
    oldId: "email",
    oldName: "Email",
    domain: "integrations" as const,
    newId: "smtp",
    newName: "SMTP",
  },
  {
    oldId: "webhooks",
    oldName: "Webhooks",
    domain: "integrations" as const,
    newId: "webhooks",
    newName: "Webhooks",
  },
  {
    oldId: "disk",
    oldName: "Disk",
    domain: "infrastructure" as const,
    newId: "disk",
    newName: "Disk",
  },
  {
    oldId: "memory",
    oldName: "Memory",
    domain: "infrastructure" as const,
    newId: "memory",
    newName: "Memory",
  },
] as const;

/**
 * Component presence (Phase 3 — replaces blanket "Not monitored" for absent systems).
 *
 * - installed: component is part of this deployment; awaiting first verification.
 * - verified: component has at least one VerificationResult on record.
 * - unavailable: component should exist but produced no verification this cycle
 *   (e.g. Runtime Doctor has not scanned yet, or a probe is not implemented).
 * - not_installed: component is genuinely absent from this deployment by design
 *   (queue service, disabled/unconfigured integration).
 *
 * Only "installed" and "verified" components appear in Runtime Health by
 * default; "unavailable" and "not_installed" are hidden behind the
 * "Show unavailable components" advanced toggle.
 */
export type ComponentPresence = "installed" | "verified" | "unavailable" | "not_installed";

export type RuntimeHealthComponent = {
  id: string;
  name: string;
  domain: RuntimeHealthDomainId;
  label: HealthLabel | NotMonitoredLabel;
  presence: ComponentPresence;
  /** Verified display when backed by probe evidence. */
  verificationLabel: string | null;
  verificationMethod: string | null;
  verificationFreshness: VerificationFreshness | null;
  durationMs: number | null;
  verifications: VerificationResult[];
  /** Why Critical / Degraded — never invent. */
  reason: string | null;
  evidence: string | null;
  recommendation: string | null;
  lastVerification: string | null;
  /** Why Not monitored / Unknown. */
  explanation: string | null;
  /** Legacy tile id when this component replaces one. */
  legacyId: string | null;
};

export type RuntimeHealthDomain = {
  id: RuntimeHealthDomainId;
  name: string;
  question: string;
  /** Aggregate domain status for summaries. */
  summary: HealthLabel | NotMonitoredLabel;
  /** User-facing summary — Verified X ago when evidence exists. */
  summaryDisplay: string;
  freshness: VerificationFreshness | null;
  lastVerifiedAt: string | null;
  /** Ops framing — operational impact in one line. */
  impactSummary: string;
  components: RuntimeHealthComponent[];
};

const NOT_MONITORED = LABEL.notMonitored as NotMonitoredLabel;

function domainSummaryDisplay(
  summary: HealthLabel | NotMonitoredLabel,
  lastVerifiedAt: string | null,
): string {
  if (summary === "Healthy" && lastVerifiedAt) {
    const age = formatVerificationAge(lastVerifiedAt);
    return age ? `Verified ${age}` : "Verified";
  }
  return summary;
}

function componentVerificationMeta(
  verifications: VerificationResult[],
  lastVerification: string | null,
): {
  verificationLabel: string | null;
  verificationMethod: string | null;
  verificationFreshness: VerificationFreshness | null;
  durationMs: number | null;
} {
  const latest = verifications.length
    ? verifications.reduce((a, b) =>
        new Date(a.timestamp).getTime() >= new Date(b.timestamp).getTime() ? a : b,
      )
    : null;
  const timestamp = latest?.timestamp ?? lastVerification;
  const category = latest?.category ?? null;
  return {
    verificationLabel: latest
      ? verificationDisplayLabel(latest)
      : timestamp
        ? `Verified ${formatVerificationAge(timestamp) ?? ""}`.trim()
        : null,
    verificationMethod: latest?.verification_method ?? null,
    verificationFreshness: timestamp
      ? computeFreshness(timestamp, new Date(), category)
      : null,
    durationMs: latest?.duration_ms ?? null,
  };
}

/** Default presence inferred from a component's health label — see ComponentPresence. */
function presenceFromLabel(label: HealthLabel | NotMonitoredLabel): ComponentPresence {
  if (label === NOT_MONITORED) return "unavailable";
  if (label === "Unknown") return "installed";
  return "verified"; // Healthy / Degraded / Critical all mean evidence exists
}

/** Human-facing presence/status text — replaces blanket "Not monitored" (Phase 3). */
export function presenceDisplayLabel(component: RuntimeHealthComponent): string {
  if (component.presence === "not_installed") return "Not Installed";
  if (component.presence === "unavailable") return "Unavailable";
  return component.label;
}

/** Only Installed/Verified components are shown by default — advanced toggle reveals the rest. */
export function isVisibleByDefault(component: RuntimeHealthComponent): boolean {
  return component.presence === "installed" || component.presence === "verified";
}

function withVerificationFields(
  base: Omit<
    RuntimeHealthComponent,
    | "verificationLabel"
    | "verificationMethod"
    | "verificationFreshness"
    | "durationMs"
    | "verifications"
    | "presence"
  >,
  verifications: VerificationResult[] = [],
  presence?: ComponentPresence,
): RuntimeHealthComponent {
  const meta = componentVerificationMeta(verifications, base.lastVerification);
  return {
    ...base,
    ...meta,
    verifications,
    presence: presence ?? presenceFromLabel(base.label),
  };
}

function integrationById(
  integrations: IntegrationStatusInput[] | null | undefined,
  id: string,
): IntegrationStatusInput | undefined {
  return integrations?.find((i) => i.id === id);
}

function fromIntegration(row: IntegrationStatusInput | undefined): HealthLabel {
  if (!row) return "Unknown";
  if (row.connected) return "Healthy";
  if (row.last_test_ok === false) return "Critical";
  return "Unknown";
}

function knowledgeLabel(
  knowledge: KnowledgeStatusInput | null | undefined,
): HealthLabel | NotMonitoredLabel {
  if (knowledge == null) return "Unknown";
  if (knowledge.ready) return "Healthy";
  if (knowledge.health_probed === false) return NOT_MONITORED;
  return "Critical";
}

function rankLabel(label: HealthLabel | NotMonitoredLabel): number {
  if (label === "Critical") return 4;
  if (label === "Degraded") return 3;
  if (label === "Unknown") return 2;
  if (label === NOT_MONITORED) return 1;
  return 0; // Healthy
}

function aggregateSummary(
  components: RuntimeHealthComponent[],
): HealthLabel | NotMonitoredLabel {
  if (components.length === 0) return NOT_MONITORED;
  let worst: HealthLabel | NotMonitoredLabel = "Healthy";
  for (const c of components) {
    if (rankLabel(c.label) > rankLabel(worst)) worst = c.label;
  }
  return worst;
}

function impactForDomain(
  domainId: RuntimeHealthDomainId,
  summary: HealthLabel | NotMonitoredLabel,
  components: RuntimeHealthComponent[],
): string {
  const critical = components.filter((c) => c.label === "Critical");
  const degraded = components.filter((c) => c.label === "Degraded");
  const monitored = components.filter((c) => c.label !== NOT_MONITORED);

  if (summary === "Critical") {
    const names = critical.map((c) => c.name).join(", ");
    return names
      ? `${RUNTIME_HEALTH_DOMAIN_META[domainId].name} impact: ${names} Critical`
      : `${RUNTIME_HEALTH_DOMAIN_META[domainId].name} requires attention`;
  }
  if (summary === "Degraded") {
    const names = degraded.map((c) => c.name).join(", ");
    return names
      ? `${RUNTIME_HEALTH_DOMAIN_META[domainId].name} degraded: ${names}`
      : `${RUNTIME_HEALTH_DOMAIN_META[domainId].name} is Degraded`;
  }
  if (summary === "Unknown") {
    return `${RUNTIME_HEALTH_DOMAIN_META[domainId].name}: verification incomplete`;
  }
  if (summary === NOT_MONITORED || monitored.length === 0) {
    return `${RUNTIME_HEALTH_DOMAIN_META[domainId].name}: no live probes yet`;
  }
  return `${RUNTIME_HEALTH_DOMAIN_META[domainId].name} operational`;
}

function verificationToHealthLabel(
  result: VerificationResult | null | undefined,
): HealthLabel | NotMonitoredLabel {
  if (!result) return NOT_MONITORED;
  if (result.status === "verified") {
    const freshness =
      result.freshness ??
      computeFreshness(result.timestamp, new Date(), result.category);
    // Expired evidence must not appear simply as Healthy.
    if (freshness === "expired") return "Degraded";
    return "Healthy";
  }
  if (result.status === "warning") return "Degraded";
  if (result.status === "failed") return "Critical";
  return "Unknown";
}

function componentFromVerification(
  id: string,
  name: string,
  domain: RuntimeHealthDomainId,
  result: VerificationResult | null | undefined,
  missingExplanation: string,
  legacyId: string | null = null,
  presenceWhenMissing: ComponentPresence = "unavailable",
): RuntimeHealthComponent {
  if (!result) {
    return notMonitoredComponent(id, name, domain, missingExplanation, legacyId, presenceWhenMissing);
  }
  const label = verificationToHealthLabel(result);
  const evidenceText =
    result.evidence.map((e) => `${e.label}: ${e.value}`).join(" · ") || null;
  return withVerificationFields(
    {
      id,
      name,
      domain,
      label,
      reason:
        label === "Critical" || label === "Degraded"
          ? result.recommendation ||
            result.evidence.find((e) => e.label === "Message")?.value ||
            `${name} verification ${result.status}`
          : null,
      evidence: evidenceText,
      recommendation: result.recommendation ?? null,
      lastVerification: result.timestamp,
      explanation:
        label === "Unknown"
          ? `${name} verification incomplete.`
          : null,
      legacyId,
    },
    [result],
  );
}

function notMonitoredComponent(
  id: string,
  name: string,
  domain: RuntimeHealthDomainId,
  explanation: string,
  legacyId: string | null = null,
  presence: ComponentPresence = "not_installed",
): RuntimeHealthComponent {
  return withVerificationFields(
    {
      id,
      name,
      domain,
      label: NOT_MONITORED,
      reason: null,
      evidence: null,
      recommendation: null,
      lastVerification: null,
      explanation,
      legacyId,
    },
    [],
    presence,
  );
}

function fromProbe(
  id: string,
  name: string,
  domain: RuntimeHealthDomainId,
  probe: RuntimeProbeInput | null | undefined,
  opts: {
    notMonitoredExplanation: string;
    legacyId?: string | null;
    fallbackRecommendation?: string | null;
    lastVerification?: string | null;
  },
): RuntimeHealthComponent {
  const {
    notMonitoredExplanation,
    legacyId = null,
    fallbackRecommendation = null,
    lastVerification = null,
  } = opts;
  if (probe == null) {
    return notMonitoredComponent(
      id,
      name,
      domain,
      notMonitoredExplanation,
      legacyId,
    );
  }
  const label = mapHealthLabel(probe.status, true);
  const message = (probe.message ?? "").trim() || null;
  const checkId = (probe.check_id ?? "").trim() || null;
  const verifiedAt =
    (probe.verified_at ?? "").trim() || lastVerification || null;
  const probeRec = (probe.recommendation ?? "").trim() || null;
  const latency =
    probe.latency_ms != null && Number.isFinite(probe.latency_ms)
      ? `${probe.latency_ms}ms`
      : null;
  const evidenceParts = [
    message,
    latency ? `latency ${latency}` : null,
    checkId ? `check ${checkId}` : null,
  ].filter(Boolean) as string[];
  const evidence = evidenceParts.length ? evidenceParts.join(" · ") : null;

  if (label === "Critical" || label === "Degraded") {
    return withVerificationFields({
      id,
      name,
      domain,
      label,
      reason: message || `${name} probe reported ${label}`,
      evidence: evidence || (checkId ? `Probe ${checkId}` : null),
      recommendation:
        probeRec ||
        fallbackRecommendation ||
        (label === "Critical"
          ? `Resolve failing ${name} verification`
          : `Review ${name} warning`),
      lastVerification: verifiedAt,
      explanation: null,
      legacyId,
    });
  }
  if (label === "Unknown") {
    return withVerificationFields({
      id,
      name,
      domain,
      label,
      reason: null,
      evidence,
      recommendation:
        probeRec || fallbackRecommendation || `Re-run verification for ${name}`,
      lastVerification: verifiedAt,
      explanation: message || `${name} verification incomplete.`,
      legacyId,
    });
  }
  return withVerificationFields({
    id,
    name,
    domain,
    label,
    reason: null,
    evidence,
    recommendation: null,
    lastVerification: verifiedAt,
    explanation: message,
    legacyId,
  });
}

function preferVerificationOrProbe(
  id: string,
  name: string,
  domain: RuntimeHealthDomainId,
  result: VerificationResult | null | undefined,
  probe: RuntimeProbeInput | null | undefined,
  missingExplanation: string,
  opts: {
    legacyId?: string | null;
    fallbackRecommendation?: string | null;
    lastVerification?: string | null;
  } = {},
): RuntimeHealthComponent {
  if (result) {
    return componentFromVerification(
      id,
      name,
      domain,
      result,
      missingExplanation,
      opts.legacyId ?? null,
    );
  }
  return fromProbe(id, name, domain, probe, {
    notMonitoredExplanation: missingExplanation,
    legacyId: opts.legacyId ?? null,
    fallbackRecommendation: opts.fallbackRecommendation ?? null,
    lastVerification: opts.lastVerification ?? null,
  });
}

function integrationComponent(
  id: string,
  name: string,
  row: IntegrationStatusInput | undefined,
  legacyId: string | null,
): RuntimeHealthComponent {
  const verifications = normalizeVerificationResults(row?.verifications);
  const label = fromIntegration(row);
  const lastVerification =
    (row?.last_tested_at ?? row?.last_verification ?? "").trim() || null;
  const message = (row?.last_message ?? "").trim() || null;
  // Not configured → genuinely absent from this deployment by design (Phase 3).
  // Status not loaded yet (!row) is distinct — treat as unavailable, not absent.
  const presence: ComponentPresence = !row
    ? "unavailable"
    : row.configured
      ? presenceFromLabel(label)
      : "not_installed";

  if (label === "Critical") {
    return withVerificationFields(
      {
        id,
        name,
        domain: "integrations",
        label,
        reason: message || `${name} connection test failed`,
        evidence: message || "Integration live test returned failure",
        recommendation: `Configure ${name} and run a successful connection test`,
        lastVerification,
        explanation: null,
        legacyId,
      },
      verifications,
      presence,
    );
  }
  if (label === "Unknown") {
    return withVerificationFields(
      {
        id,
        name,
        domain: "integrations",
        label,
        reason: null,
        evidence: null,
        recommendation: row?.configured
          ? `Run a live connection test for ${name}`
          : `Configure ${name}`,
        lastVerification,
        explanation: !row
          ? `${name} status not loaded.`
          : row.configured
            ? `${name} has no verified live test yet.`
            : `${name} is not configured for this tenant.`,
        legacyId,
      },
      verifications,
      presence,
    );
  }
  return withVerificationFields(
    {
      id,
      name,
      domain: "integrations",
      label,
      reason: null,
      evidence: null,
      recommendation: null,
      lastVerification,
      explanation: null,
      legacyId,
    },
    verifications,
    presence,
  );
}

/**
 * Derive Runtime Health as four operational domains from existing evidence only.
 */
export function deriveRuntimeHealthDomains(input: {
  customer: CustomerStatusInput;
  tenantHealth: TenantHealthInput | null;
  integrations?: IntegrationStatusInput[] | null;
  knowledge?: KnowledgeStatusInput | null;
  dns?: DnsStatusInput | null;
  ssl?: SslStatusInput | null;
  /** Platform-domain VerificationResult records from Control Plane probes. */
  platformVerifications?: VerificationResult[] | null;
  /** Optional platform Critical evidence (attention / doctor) — never invent. */
  attentionProblem?: string | null;
  doctorMessage?: string | null;
  recommendedAction?: string | null;
  lastVerification?: string | null;
}): RuntimeHealthDomain[] {
  const {
    customer,
    tenantHealth,
    integrations,
    knowledge,
    dns,
    ssl,
    platformVerifications,
    attentionProblem,
    doctorMessage,
    recommendedAction,
    lastVerification,
  } = input;

  const platformByComponent = new Map(
    normalizeVerificationResults(platformVerifications).map((v) => [v.component, v]),
  );
  const probes = tenantHealth?.runtime_probes;
  const verifiedAt = lastVerification ?? null;

  const conciergeLabel = resolveTenantHealthLabel({
    opsHealth: tenantHealth?.health,
    registryHealth: customer.last_health_status,
    hasStack: tenantHealth?.has_stack,
    hasRegistryObservation: Boolean(customer.last_health_status),
  });

  const platformComponents: RuntimeHealthComponent[] = [
    preferVerificationOrProbe(
      "control_plane_backend",
      "Control Plane Backend",
      "platform",
      platformByComponent.get("control_plane_backend"),
      probes?.control_plane_backend,
      "Control Plane Backend probe missing from operations payload.",
      { fallbackRecommendation: recommendedAction, lastVerification: verifiedAt },
    ),
    preferVerificationOrProbe(
      "operations_api",
      "Operations API",
      "platform",
      platformByComponent.get("operations_api"),
      probes?.operations_api,
      "Operations API probe missing from operations payload.",
      { fallbackRecommendation: recommendedAction, lastVerification: verifiedAt },
    ),
    preferVerificationOrProbe(
      "authentication",
      "Authentication",
      "platform",
      platformByComponent.get("authentication"),
      probes?.authentication,
      "Authentication probe missing from operations payload.",
      { fallbackRecommendation: recommendedAction, lastVerification: verifiedAt },
    ),
    preferVerificationOrProbe(
      "internal_platform_services",
      "Internal Platform Services",
      "platform",
      platformByComponent.get("internal_platform_services"),
      probes?.internal_platform_services,
      "Internal Platform Services probe missing from operations payload.",
      { fallbackRecommendation: recommendedAction, lastVerification: verifiedAt },
    ),
  ];

  const conciergeReason =
    conciergeLabel === "Critical" || conciergeLabel === "Degraded"
      ? (attentionProblem ?? "").trim() ||
        (conciergeLabel === "Critical"
          ? "Concierge Runtime unavailable"
          : "Concierge Runtime degraded")
      : null;
  const conciergeEvidence =
    conciergeLabel === "Critical" || conciergeLabel === "Degraded"
      ? (doctorMessage ?? "").trim() ||
        (conciergeLabel === "Critical"
          ? "Runtime Doctor failed Concierge Runtime health check"
          : "Concierge Runtime health is Degraded")
      : null;
  const conciergeRecommendation =
    conciergeLabel === "Critical" || conciergeLabel === "Degraded"
      ? (recommendedAction ?? "").trim() ||
        "Open Runtime Doctor report and resolve failing checks"
      : null;

  const kbLabel = knowledgeLabel(knowledge);
  const kbComponent: RuntimeHealthComponent = withVerificationFields(
    {
      id: "knowledge_base",
      name: "Knowledge Base",
      domain: "tenant_runtime",
      label: kbLabel,
      reason:
        kbLabel === "Critical"
          ? (knowledge?.detail ?? "Knowledge Base not ready").trim() ||
            "Knowledge Base not ready"
          : null,
      evidence:
        kbLabel === "Critical"
          ? (knowledge?.last_verify_message ?? knowledge?.detail ?? null)
          : null,
      recommendation:
        kbLabel === "Critical"
          ? "Upload manuals, reindex, and verify Knowledge Base readiness"
          : kbLabel === "Unknown"
            ? "Load Knowledge Base status"
            : null,
      lastVerification: knowledge?.last_verified_at ?? null,
      explanation:
        kbLabel === NOT_MONITORED
          ? "Knowledge Base health probe not available — Not monitored."
          : kbLabel === "Unknown"
            ? "Knowledge Base has no verified observation yet."
            : null,
      legacyId: null,
    },
  );

  const tenantRuntimeComponents: RuntimeHealthComponent[] = [
    withVerificationFields({
      id: "concierge_runtime",
      name: "Concierge Runtime",
      domain: "tenant_runtime",
      label: conciergeLabel,
      reason: conciergeReason,
      evidence: conciergeEvidence,
      recommendation: conciergeRecommendation,
      lastVerification: verifiedAt,
      explanation:
        conciergeLabel === "Unknown"
          ? "Concierge Runtime has no verified observation yet."
          : null,
      legacyId: "api",
    }),
    kbComponent,
  ];

  // Email Processing — only when probe present (configured). Never show Not monitored stub.
  if (probes && "email_processing" in probes && probes.email_processing != null) {
    tenantRuntimeComponents.push(
      fromProbe(
        "email_processing",
        "Email Processing",
        "tenant_runtime",
        probes.email_processing,
        {
          notMonitoredExplanation: "",
          fallbackRecommendation: "Fix IMAP/SMTP connectivity",
          lastVerification: verifiedAt,
        },
      ),
    );
  }

  // Worker — show when probe present; if stack exists but probe omitted, Unknown not Not monitored.
  if (probes?.worker != null) {
    tenantRuntimeComponents.push(
      fromProbe("worker", "Worker", "tenant_runtime", probes.worker, {
        notMonitoredExplanation: "",
        fallbackRecommendation: "Restart tenant API workers",
        lastVerification: verifiedAt,
      }),
    );
  } else if (tenantHealth?.has_stack) {
    tenantRuntimeComponents.push(
      withVerificationFields({
        id: "worker",
        name: "Worker",
        domain: "tenant_runtime",
        label: "Unknown",
        reason: null,
        evidence: null,
        recommendation: "Refresh operations to run worker continuous verification",
        lastVerification: verifiedAt,
        explanation: "Worker probe not included in operations payload yet.",
        legacyId: null,
      }),
    );
  }

  // Integrations: existing openai/email/webhooks tiles + any other loaded integrations.
  // Never place integrations under Platform.
  const integrationComponents: RuntimeHealthComponent[] = [];
  const seenIntegrationIds = new Set<string>();

  const ensureIntegration = (
    sourceId: string,
    displayName: string,
    componentId: string,
    legacyId: string | null,
  ) => {
    if (seenIntegrationIds.has(componentId)) return;
    seenIntegrationIds.add(componentId);
    integrationComponents.push(
      integrationComponent(
        componentId,
        displayName,
        integrationById(integrations, sourceId),
        legacyId,
      ),
    );
  };

  ensureIntegration("openai", "OpenAI", "openai", "ai");
  ensureIntegration("email", "SMTP", "smtp", "email");
  ensureIntegration("webhooks", "Webhooks", "webhooks", "webhooks");

  if (integrations) {
    for (const row of integrations) {
      if (row.id === "openai" || row.id === "email" || row.id === "webhooks") {
        continue;
      }
      ensureIntegration(row.id, row.name || row.id, row.id, null);
    }
  } else {
    ensureIntegration("shopify", "Shopify", "shopify", null);
    ensureIntegration("stripe", "Stripe", "stripe", null);
  }

  const doctorByCheck = new Map(
    normalizeVerificationResults(tenantHealth?.verifications).map((v) => [
      v.component,
      v,
    ]),
  );
  const dnsVerifications = normalizeVerificationResults(dns?.verifications);
  const sslVerifications = normalizeVerificationResults(ssl?.verifications);
  const dnsLabel: HealthLabel | NotMonitoredLabel =
    !dns || dns.state === "not_configured"
      ? "Unknown"
      : dns.ok && dns.state === "verified"
        ? "Healthy"
        : dns.state === "failed"
          ? "Critical"
          : "Unknown";

  const infrastructureComponents: RuntimeHealthComponent[] = [
    withVerificationFields(
      {
        id: "dns_connectivity",
        name: "Public Connectivity",
        domain: "infrastructure",
        label: dnsLabel,
        reason:
          dnsLabel === "Critical"
            ? (dns?.last_message ?? "Public connectivity verification failed")
            : null,
        evidence:
          dnsLabel === "Critical"
            ? (dns?.last_message ?? null)
            : dns?.last_message ?? null,
        recommendation:
          dnsLabel === "Critical"
            ? "Run Public Connectivity verification and confirm DNS, HTTPS, TLS, and /health"
            : dnsLabel === "Unknown"
              ? "Run Public Connectivity verification"
              : null,
        lastVerification: dns?.last_tested_at ?? null,
        explanation:
          !dns || dns.state === "not_configured"
            ? "Public connectivity has not been tested yet."
            : null,
        legacyId: null,
      },
      dnsVerifications,
    ),
    withVerificationFields(
      {
        id: "tls_certificate",
        name: "TLS Certificate",
        domain: "infrastructure",
        label:
          !ssl || ssl.state === "not_configured"
            ? "Unknown"
            : ssl.ok && (ssl.state === "verified" || ssl.state === "expiring_soon")
              ? ssl.state === "expiring_soon"
                ? "Degraded"
                : "Healthy"
              : ssl.state === "failed"
                ? "Critical"
                : "Unknown",
        reason:
          ssl?.state === "failed"
            ? (ssl.last_message ?? "TLS verification failed")
            : ssl?.state === "expiring_soon"
              ? (ssl.last_message ?? "Certificate expiring soon")
              : null,
        evidence: ssl?.last_message ?? null,
        recommendation:
          ssl?.state === "failed"
            ? "Run Test SSL and renew/reissue TLS"
            : ssl?.state === "expiring_soon"
              ? "Renew TLS certificate before expiry"
              : !ssl || ssl.state === "not_configured"
                ? "Run Test SSL"
                : null,
        lastVerification: ssl?.last_tested_at ?? null,
        explanation:
          !ssl || ssl.state === "not_configured"
            ? "TLS has not been tested yet."
            : null,
        legacyId: null,
      },
      sslVerifications,
    ),
    preferVerificationOrProbe(
      "postgresql",
      "PostgreSQL",
      "infrastructure",
      doctorByCheck.get("database.connect"),
      probes?.database,
      "PostgreSQL probe missing — run Runtime Doctor / refresh operations.",
      {
        legacyId: "database",
        fallbackRecommendation: recommendedAction,
        lastVerification: verifiedAt,
      },
    ),
    preferVerificationOrProbe(
      "disk",
      "Disk",
      "infrastructure",
      doctorByCheck.get("host.disk_space"),
      probes?.disk,
      "Disk probe missing — run Runtime Doctor / refresh operations.",
      {
        legacyId: "disk",
        fallbackRecommendation: recommendedAction,
        lastVerification: verifiedAt,
      },
    ),
    preferVerificationOrProbe(
      "storage",
      "Storage Health",
      "infrastructure",
      doctorByCheck.get("host.docker_storage"),
      null,
      "Storage Health probe missing — run Runtime Doctor / refresh operations.",
      {
        fallbackRecommendation:
          "Confirm post-deploy Docker cleanup and review unused images / BuildKit cache",
        lastVerification: verifiedAt,
      },
    ),
    preferVerificationOrProbe(
      "memory",
      "Memory",
      "infrastructure",
      doctorByCheck.get("host.memory"),
      probes?.memory,
      "Memory probe missing — run Runtime Doctor / refresh operations.",
      {
        legacyId: "memory",
        fallbackRecommendation: recommendedAction,
        lastVerification: verifiedAt,
      },
    ),
    preferVerificationOrProbe(
      "docker",
      "Docker",
      "infrastructure",
      doctorByCheck.get("host.docker") ?? doctorByCheck.get("infra.docker"),
      probes?.docker,
      "Docker probe missing — refresh operations continuous verification.",
      {
        fallbackRecommendation: "Inspect Docker daemon and tenant containers",
        lastVerification: verifiedAt,
      },
    ),
    preferVerificationOrProbe(
      "cpu",
      "CPU",
      "infrastructure",
      doctorByCheck.get("host.cpu"),
      probes?.cpu,
      "CPU probe missing — refresh operations continuous verification.",
      {
        fallbackRecommendation: "Investigate host CPU load",
        lastVerification: verifiedAt,
      },
    ),
  ];

  const byDomain: Record<RuntimeHealthDomainId, RuntimeHealthComponent[]> = {
    platform: platformComponents,
    tenant_runtime: tenantRuntimeComponents,
    integrations: integrationComponents,
    infrastructure: infrastructureComponents,
  };

  return RUNTIME_HEALTH_DOMAIN_ORDER.map((id) => {
    const components = byDomain[id];
    const summary = aggregateSummary(components);
    const meta = RUNTIME_HEALTH_DOMAIN_META[id];
    const lastVerifiedAt = components
      .map((c) => c.lastVerification)
      .filter(Boolean)
      .sort(
        (a, b) => new Date(String(b)).getTime() - new Date(String(a)).getTime(),
      )[0] ?? null;
    const freshness = lastVerifiedAt ? computeFreshness(lastVerifiedAt) : null;
    return {
      id,
      name: meta.name,
      question: meta.question,
      summary,
      summaryDisplay: domainSummaryDisplay(summary, lastVerifiedAt),
      freshness,
      lastVerifiedAt,
      impactSummary: impactForDomain(id, summary, components),
      components,
    };
  });
}

/** Flatten domains → legacy RuntimeHealthRow[] (compat / list views). */
export function flattenRuntimeHealthDomains(
  domains: RuntimeHealthDomain[],
): RuntimeHealthRow[] {
  return domains.flatMap((d) =>
    d.components.map((c) => ({
      id: c.id,
      name: c.name,
      label: c.label,
    })),
  );
}

/** Assert every component belongs to exactly one domain (test helper / guard). */
export function assertExclusiveDomainMembership(
  domains: RuntimeHealthDomain[],
): { ok: true } | { ok: false; duplicates: string[]; missingDomains: string[] } {
  const seen = new Map<string, string>();
  const duplicates: string[] = [];
  for (const d of domains) {
    for (const c of d.components) {
      if (c.domain !== d.id) {
        duplicates.push(`${c.id} domain mismatch`);
      }
      if (seen.has(c.id)) {
        duplicates.push(`${c.id} in ${seen.get(c.id)} and ${d.id}`);
      } else {
        seen.set(c.id, d.id);
      }
    }
  }
  const missingDomains = RUNTIME_HEALTH_DOMAIN_ORDER.filter(
    (id) => !domains.some((d) => d.id === id),
  );
  if (duplicates.length || missingDomains.length) {
    return { ok: false, duplicates, missingDomains };
  }
  return { ok: true };
}
